import { z } from "zod";

/**
 * Kanonik KAYIT ERİŞİMİ (CLOS-91 `[077]`).
 *
 * ### ⚠ Permission ile kayıt erişimi AYRI iki sorudur
 *
 * Kart: *"rol permission'ı OTOMATİK KAYIT ERİŞİMİ YARATMAZ."* Permission *"bu kişi
 * sözleşme düzenleyebilir mi?"* sorusunu, kayıt erişimi *"HANGİ sözleşmeyi?"* sorusunu
 * cevaplıyor. Tek soruya indirgeseydik, sözleşme düzenleme yetkisi olan herkes
 * organizasyondaki her sözleşmeyi görürdü.
 *
 * ### Tek genel model, domain başına doğrulayıcı
 *
 * Kart: *"sözleşme, müvekkil ve şablon domainleri bu genel sözleşmeye KENDİ kayıt türü,
 * sahiplik ve erişilebilirlik DOĞRULAYICILARINI bağlar."* Domain başına ayrı erişim
 * tablosu yazsaydık, `[079]`'un tek karar sözleşmesi üç ayrı sorguya bölünür ve biri gün
 * gelip diğerlerinden ayrışırdı.
 */

export const RECORD_ACCESS_LEVELS = ["read", "write"] as const;
export type RecordAccessLevel = (typeof RECORD_ACCESS_LEVELS)[number];

export const RECORD_ACCESS_LEVEL_NAMES: Record<RecordAccessLevel, string> = {
  read: "Görüntüleme",
  write: "Düzenleme",
};

/**
 * Erişimin KAYNAĞI — kart: *"erişim seviyesi ve KAYNAK bilgisini koruma."*
 *
 * `creator` bir açık verme değil, kanonik bir kaynak: kaydı oluşturan kişi ona erişir,
 * yoksa kendi oluşturduğu sözleşmeyi göremez ve `[077]`'nin *"veren aktör hedef kayıt için
 * kayıt erişimine sahip olmalıdır"* kuralı hiçbir zaman sağlanamazdı — ilk erişimi kimse
 * veremezdi.
 *
 * ⚠ `responsibility` BİLEREK YOK: `[074]` CLOS-112 açıkça *"sorumlu atanmak kayıt erişimini
 * otomatik vermez"* diyor.
 */
/**
 * ⚠ `assignment` (CLOS-1185): erişim SÖZLEŞME BAZLI HİZMET ATAMASINDAN doğuyor. Ayrı bir
 * kaynak değeri, çünkü kapanışı da ayrı bir olaya bağlı: atama biter ya da hizmet ilişkisi
 * askıya alınırsa erişim düşer. `grant` altına saklasaydık "bu erişim neden var?" sorusu
 * cevapsız kalır ve atamayla birlikte kapatılması gereken satır sessizce kalırdı.
 */
/**
 * ⚠ `automatic_policy` — ERİŞİM PROFİLİNDEN doğan otomatik erişim (CLOS-1217, §11).
 *
 * Diğer dördünden ayrı bir kaynak olması şart: profil düzenlemesinin toplu uygulanması
 * (§11.5) YALNIZ bu kaynaklı satırları değiştirebilir. `grant` ile aynı kutuya koysaydık,
 * bir profil güncellemesi kullanıcının elle verdiği erişimleri de silerdi — ve hangi
 * satırın nereden geldiği bir daha okunamazdı.
 */
export const RECORD_ACCESS_SOURCES = [
  "creator",
  "grant",
  "guest",
  "assignment",
  "automatic_policy",
  /**
   * ⚠ `contract_manager` — SÖZLEŞME YÖNETİCİSİ ATAMASINDAN doğan erişim (CLOS-1212,
   * Final Kanonik v1.0 §12.3).
   *
   * Deponun eski kararı ("sorumlu olmak erişim yaratmaz") SUPERSEDED. Gerekçe kilitli
   * kararda: kişiyi sözleşmenin yöneticisi yapıp sözleşmeyi görememesini beklemek anlamsız.
   * Atama **NEREDE** sorusunu cevaplar; rol/permission **NE** sorusunu. Yönetici olmak
   * bütün yetkileri, workflow bypass'ını, onay ya da imza yetkisini vermez.
   *
   * ⚠ Ayrı kaynak olması şart: yönetici DEĞİŞTİĞİNDE eski yöneticinin erişimi yalnız bu
   * kaynaktan geliyorsa kalkmalı, elle verilmiş bir erişimi varsa DEVAM etmeli. `grant` ile
   * aynı kutuya koysaydık ikisini ayırt edemez ve manuel erişimi de silerdik.
   */
  "contract_manager",
  /**
   * ⚠ `contract_department` — SÖZLEŞMEDE ÇALIŞAN EKİPTEN doğan erişim (CLOS-1223,
   * Departmanlar Kanonu §10.1).
   *
   * §6.3: aktif katılım Effective Contract Team üyelerine kayıt erişimi verir. Kaynağın
   * AYRI olması şart (§10.3): ekip çıkarıldığında ya da kişi gerçek departmandan
   * ayrıldığında **yalnız bu kaynaklı** satır kalkmalı. `grant` ile aynı kutuya koysaydık,
   * bir ekip değişikliği elle verilmiş erişimleri de silerdi.
   *
   * ⚠ Seviye `read`: ekipte olmak GÖRÜNÜRLÜK verir. Yazma her zaman ayrı ve açık bir
   * karar — `automatic_policy` ile aynı çizgi. Yönetici ataması (`contract_manager`)
   * `write` veriyor çünkü orada kişi sözleşmenin sorumlusu.
   */
  "contract_department",
] as const;
export type RecordAccessSource = (typeof RECORD_ACCESS_SOURCES)[number];

/**
 * ERİŞİM TÜRÜ — projection'ın kullanıcıya dönük ekseni (CLOS-1127 · Halit K1/K3 · T.2 #11).
 *
 * `record_access` satırının KAYNAĞI (`source`) ve ÖZNESİ tek bir okunur türe iniyor:
 * `creator` (oluşturan), `internal` (organizasyon üyesine verilen), `guest` (haricî misafir
 * daveti kabulü — `source='guest'` + `guest_invitation_id`), `client_area` (Müvekkil Alanı
 * paylaşımı) ve `party` (hukuki taraf katılımı, `participant_id`). Sözleşme Erişimi modalı
 * yalnız `creator`/`internal`/`guest` çizer; alan ve taraf satırları kendi yüzeylerinde.
 * Çözülemeyen özne (silinmiş kullanıcı) aktif listeye GİRMEZ; audit/history'de kalır.
 */
export const RECORD_ACCESS_KINDS = [
  "creator",
  "internal",
  "guest",
  "client_area",
  "party",
] as const;
export type RecordAccessKind = (typeof RECORD_ACCESS_KINDS)[number];

export const RECORD_ACCESS_KIND_NAMES: Record<RecordAccessKind, string> = {
  creator: "Oluşturan",
  internal: "Organizasyon üyesi",
  guest: "Haricî misafir",
  client_area: "Müvekkil Alanı",
  party: "Hukuki taraf",
};

/**
 * ERİŞİM DURUMU — davet durumuyla KARIŞTIRILMAZ (Halit: iki eksen). Kabul edilip sonra
 * kaldırılan misafir "Davet: Kabul edildi · Erişim: Kaldırıldı" okunur. `expired` planlanan
 * bitiş geçmiş (worker henüz kapatmamış olsa da) ya da `closed_reason='expired'`;
 * `revoked` diğer kapanışlar (revoke, üyelik sonu).
 */
export const RECORD_ACCESS_STATES = ["active", "expired", "revoked"] as const;
export type RecordAccessState = (typeof RECORD_ACCESS_STATES)[number];

export const RECORD_ACCESS_STATE_NAMES: Record<RecordAccessState, string> = {
  active: "Aktif",
  expired: "Süresi doldu",
  revoked: "Kaldırıldı",
};

/**
 * ⚠ `client_area` ÜÇÜNCÜ ÖZNE (ADR-0038, FAZ 0B).
 *
 * Grant bir KİŞİYE değil MÜVEKKİL ALANINA veriliyor; kimin göreceği okuma anında o alanın
 * AKTİF temsilciliğinden türüyor. Kişi bazlı kopyalasaydık, sonradan aktifleşen temsilci
 * için yeniden paylaşım gerekir ve revoke N noktaya dağılırdı.
 */
/**
 * ⚠ `provider` (CLOS-1185): özne SAĞLAYICI ORGANİZASYONUN KENDİSİ, tek tek kullanıcılar
 * değil — `client_area` deseninin aynısı. Sağlayıcı içinde kimin çalışacağını sağlayıcı
 * belirler; sözleşme sahibi onların üye listesini ne bilir ne de bilmelidir.
 */
export const RECORD_SUBJECT_TYPES = ["user", "membership", "client_area", "provider"] as const;
export type RecordSubjectType = (typeof RECORD_SUBJECT_TYPES)[number];

/**
 * ÖZNE ANAHTARI — erişimin kime verildiğinin tek metni.
 *
 * Tekillik kısıtı bunun üzerinde: aynı kişiye aynı kayıt için ikinci bir satır açmak,
 * hangi seviyenin geçerli olduğunu belirsiz bırakırdı (`[073]` kimlik anahtarı ve `[075]`
 * slot anahtarıyla aynı desen).
 *
 * ⚠ Önek burada ÇAKIŞMA ÖNLEMİYOR — kullanıcı ve üyelik kimlikleri zaten farklı rastgele
 * UUID'ler. Önek anahtarı KENDİNİ AÇIKLAR yapıyor: audit kaydında ve `[079]`'un sorgusunda
 * "bu erişim kime?" sorusu ikinci bir tabloya bakmadan cevaplanıyor. (`[073]`'te önek
 * gerçek bir çakışmayı engelliyordu; buradaki gerekçe farklı ve daha zayıf.)
 */
export function recordSubjectKey(subjectType: RecordSubjectType, subjectId: string): string {
  return `${subjectType}:${subjectId}`;
}

/**
 * ELLE VERİLEBİLEN ÖZNE TÜRLERİ — depolama enum'undan DAR (CLOS-1185).
 *
 * ⚠ `provider` BİLEREK DIŞARIDA. Sağlayıcı erişimi bir kullanıcının paylaşım kararından
 * değil, SÖZLEŞME BAZLI ATAMADAN doğar ve atamayla birlikte kapanır. Elle verilebilseydi
 * atamasız bir sağlayıcı erişimi doğar, atama bittiğinde de kapanmazdı — "bu sağlayıcı
 * burayı neden görüyor?" sorusu cevapsız kalırdı.
 */
export const GRANTABLE_SUBJECT_TYPES = ["user", "membership", "client_area"] as const;

export const grantRecordAccessSchema = z.object({
  subjectType: z.enum(GRANTABLE_SUBJECT_TYPES, "Geçerli bir özne türü seçilmelidir"),
  subjectId: z.uuid("Geçerli bir kullanıcı veya üyelik seçilmelidir"),
  level: z.enum(RECORD_ACCESS_LEVELS, "Geçerli bir erişim seviyesi seçilmelidir"),
  /**
   * Bitiş zamanı OPSİYONEL. Kart: *"varsa bitiş zamanı."* Süreli erişimin yaşam döngüsü
   * `[078]` CLOS-92'nin ama kolon kanonik sözleşmenin parçası ve burada doğuyor.
   */
  endsAt: z.iso.datetime({ message: "Geçerli bir bitiş zamanı giriniz" }).nullish(),
});
export type GrantRecordAccessInput = z.infer<typeof grantRecordAccessSchema>;

/**
 * Erişimin NASIL bittiği (CLOS-92 `[078]`).
 *
 * ⚠ `ends_at` ile `closed_at` AYRI iki şey: birincisi PLANLANAN bitiş, ikincisi GERÇEKLEŞEN
 * kapanış. Tek kolona sıkıştırsaydık "süresi doldu" ile "yönetici kaldırdı" ayırt edilemez
 * olurdu — oysa biri beklenen bir son, diğeri bir karardır ve denetimde ikisi ayrı sorular.
 */
export const RECORD_ACCESS_CLOSE_REASONS = ["revoked", "expired", "membership_ended"] as const;
export type RecordAccessCloseReason = (typeof RECORD_ACCESS_CLOSE_REASONS)[number];

export const RECORD_ACCESS_CLOSE_REASON_NAMES: Record<RecordAccessCloseReason, string> = {
  revoked: "Kaldırıldı",
  expired: "Süresi doldu",
  membership_ended: "Üyelik sona erdi",
};

/**
 * Erişim YENİLEME (CLOS-92 `[078]`).
 *
 * ⚠ Kart: *"yenileme, SONA ERMİŞ erişimi SESSİZCE CANLANDIRMAZ; aktör ve hedef uygunluğu
 * yeniden doğrulanır."* Bu yüzden yenileme kapanmış bir satırı açmıyor — kapanmışsa yeni
 * bir verme gerekiyor ve o yol bütün kapılardan yeniden geçiyor.
 */
export const renewRecordAccessSchema = z.object({
  endsAt: z.iso.datetime({ message: "Geçerli bir bitiş zamanı giriniz" }).nullable(),
});
export type RenewRecordAccessInput = z.infer<typeof renewRecordAccessSchema>;

export type RecordAccessEntry = {
  id: string;
  recordType: string;
  recordId: string;
  subjectType: RecordSubjectType;
  subjectId: string;
  /** Öznenin görünen adı — okuma yolunda yetkiyle çözülüyor, satırda saklanmıyor. */
  subjectName: string | null;
  level: RecordAccessLevel;
  source: RecordAccessSource;
  /** Kullanıcıya dönük tür (CLOS-1127) — kaynak + özne + katılım/davet bağından türetilir. */
  kind: RecordAccessKind;
  /** Erişim durumu; davet durumundan bağımsız (CLOS-1127). */
  accessState: RecordAccessState;
  /** Haricî misafir satırında kabul edilen davetin kimliği; diğerlerinde `null`. */
  guestInvitationId: string | null;
  grantedBy: string | null;
  startsAt: string;
  endsAt: string | null;
  closedAt: string | null;
  closedReason: RecordAccessCloseReason | null;
};
